import React from "react";
import { Info, Send, Eye, Users, MousePointerClick, MessageCircle, Percent, MapPin, Trophy } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { fmtCtr } from "@/lib/api";

const n = (v) => Number(v || 0).toLocaleString("en-IN");
export const METRICS = [
  ["delivered", Send, "impressions"], ["views", Eye, "views"], ["reach", Users, "unique_reach"],
  ["clicks", MousePointerClick, "clicks"], ["wa", MessageCircle, "whatsapp_clicks"],
];

// Tap-only definitions + tracking limits; nothing long is shown permanently.
export function EyeInfo({ adId, rep, E, locale }) {
  const d = (v) => new Date(v).toLocaleDateString(locale, { day: "numeric", month: "short", year: "numeric" });
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" aria-label={E.infoBtn} className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-white hover:bg-emerald-50 text-emerald-800 px-2.5 py-1 text-xs font-semibold" data-testid={`eye-info-btn-${adId}`}>
          <Info className="w-3.5 h-3.5" /> <span className="min-[400px]:hidden">{E.infoShort}</span><span className="hidden min-[400px]:inline">{E.infoBtn}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" collisionPadding={12} className="w-[min(22rem,calc(100vw-1.5rem))] max-h-[70vh] overflow-y-auto rounded-2xl p-4 text-xs" data-testid={`eye-info-panel-${adId}`}>
        <div className="font-bold text-sm text-slate-900 mb-2">{E.infoTitle}</div>
        <ul className="space-y-2">
          {[...METRICS.map((m) => m[0]), "ctr"].map((k) => (
            <li key={k}><div className="font-semibold text-slate-800">{E.m[k].long} <span className="text-[10px] font-normal text-slate-400">({E.m[k].en})</span></div><div className="text-slate-600">{E.m[k].h}</div></li>
          ))}
        </ul>
        <div className="mt-3 pt-2 border-t border-slate-100 space-y-1 text-slate-500">
          <div>{E.real}</div>
          {rep.views_tracking_since && <div>{E.viewsSince} {d(rep.views_tracking_since)}</div>}
          {rep.reach_tracking_since && <div>{E.reachSince} {d(rep.reach_tracking_since)}; {E.reachBefore}</div>}
          {rep.tracking_since && <div>{E.firstEv} {d(rep.tracking_since)}</div>}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function MetricCard({ k, Icon, value, E, hot, testid }) {
  const m = E.m[k];
  return (
    <div className={`rounded-2xl border p-3 sm:p-4 min-w-0 flex flex-col ${hot ? "bg-emerald-50 border-emerald-300" : "bg-white border-slate-200"}`} data-testid={testid}>
      <div className="flex flex-col min-[420px]:flex-row min-[420px]:items-center gap-1 min-[420px]:gap-2 min-w-0">
        <span className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${hot ? "bg-emerald-700 text-white" : "bg-emerald-50 text-emerald-700"}`}><Icon className="w-4 h-4" /></span>
        <span className="text-[10px] uppercase text-slate-400 font-semibold leading-tight min-w-0">{m.en}</span>
      </div>
      <div className="mt-2 text-2xl sm:text-3xl font-extrabold text-emerald-800 tracking-tight leading-none" data-testid={`${testid}-value`}>{value}</div>
      <div className="mt-1.5 text-[13px] font-bold text-slate-800 leading-snug break-words">{m.t}</div>
      <div className="mt-0.5 text-[11px] text-slate-500 leading-snug break-words">{m.h}</div>
    </div>
  );
}

export function SummaryCards({ adId, rep, E, notDelivered }) {
  const t = rep.totals;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 sm:gap-3" data-testid={`eye-summary-${adId}`}>
      {METRICS.map(([k, Icon, f]) => (
        <MetricCard key={k} k={k} Icon={Icon} E={E} hot={k === "views"} testid={`eye-metric-${k}-${adId}`} value={notDelivered ? "0" : t[f] === null || t[f] === undefined ? "—" : n(t[f])} />
      ))}
      <MetricCard k="ctr" Icon={Percent} E={E} testid={`eye-metric-ctr-${adId}`} value={notDelivered || t.ctr === null || t.ctr === undefined ? "—" : fmtCtr(t.ctr)} />
    </div>
  );
}

function Stat({ k, value, E, testid }) {
  return (
    <div className="rounded-xl bg-slate-50 px-2.5 py-2 min-w-0" data-testid={testid}>
      <div className="text-lg font-extrabold text-emerald-800 leading-none">{value}</div>
      <div className="mt-1 text-[11px] text-slate-600 leading-snug break-words">{E.m[k].long}</div>
    </div>
  );
}

export function PinCards({ adId, rep, E }) {
  const best = new Set(rep.best_pins);
  const t = rep.totals;
  return (
    <div data-testid={`eye-pins-${adId}`}>
      <div className="rounded-2xl border border-emerald-200 bg-emerald-50/70 p-3 sm:p-4 mb-3" data-testid={`eye-pin-overall-${adId}`}>
        <div className="font-bold text-sm text-emerald-900">{E.overall}</div>
        <div className="text-[11px] text-emerald-800/80 mb-2">{E.overallNote}</div>
        <div className="grid grid-cols-2 min-[480px]:grid-cols-3 lg:grid-cols-5 gap-2">
          {METRICS.map(([k, , f]) => <Stat key={k} k={k} E={E} value={t[f] === null || t[f] === undefined ? "—" : n(t[f])} testid={`eye-overall-${k}-${adId}`} />)}
        </div>
      </div>
      <div className="font-bold text-sm text-slate-800 mb-2">{E.pins}</div>
      <div className="grid gap-3 md:grid-cols-2">
        {rep.pins.map((p) => (
          <div key={p.pincode} className={`rounded-2xl border bg-white p-3 sm:p-4 min-w-0 ${best.has(p.pincode) ? "border-amber-300" : "border-slate-200"}`} data-testid={`eye-pin-card-${p.pincode}`}>
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="min-w-0">
                <div className="font-display text-lg font-bold text-slate-900 inline-flex items-center gap-1"><MapPin className="w-4 h-4 text-emerald-700" />{p.pincode}</div>
                <div className="text-xs text-slate-500 break-words">{p.district ? [p.locality, p.district, p.state].filter(Boolean).join(", ") : E.unverified}</div>
              </div>
              {best.has(p.pincode) && <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-900 px-2 py-0.5 text-[11px] font-semibold flex-shrink-0" data-testid={`eye-pin-best-${p.pincode}`}><Trophy className="w-3 h-3" />{E.best}</span>}
            </div>
            <div className="grid grid-cols-2 min-[480px]:grid-cols-3 gap-2">
              {METRICS.map(([k, , f]) => <Stat key={k} k={k} E={E} value={n(f === "unique_reach" ? p.unique_reach ?? p.unique_viewers : p[f])} testid={`eye-pin-${k}-${p.pincode}`} />)}
              <Stat k="ctr" E={E} value={p.ctr === null || p.ctr === undefined ? "—" : fmtCtr(p.ctr)} testid={`eye-pin-ctr-${p.pincode}`} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
